import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const TrustPage = ({ comingSoonMode = false }) => {
  return (
    <div className="min-h-screen bg-white">
      {!comingSoonMode && <Navigation />}

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1600&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.2)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 via-blue-800/70 to-blue-900/90 z-0" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="mb-6">
            <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold inline-block">
              🔒 Your Data, Your Business
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 drop-shadow-lg">
            Trust & Security
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Clear answers about data ownership, security, and what happens if things change. 
            No vague promises—just straight talk from a fellow service business owner.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Data Ownership */}
          <div className="mb-12">
            <div className="flex items-center mb-4">
              <div className="text-3xl mr-4">📋</div>
              <h2 className="text-2xl font-bold text-gray-900">You Own Your Data. Period.</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <p className="text-gray-700 mb-4">
                Your customer list, job history, invoices, notes, and financial records belong to you. 
                SuddyFlow is the custodian—we store and protect it—but we never claim ownership of your business data.
              </p>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <p className="font-semibold text-green-800 mb-2">What this means in practice:</p>
                <ul className="text-gray-700 space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>Export your data anytime—CSV, JSON, whatever you need</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>We never sell, share, or monetize your customer information</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>Cancel your subscription? Your data stays yours—download it before you go</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* What If SuddyFlow Shuts Down */}
          <div className="mb-12">
            <div className="flex items-center mb-4">
              <div className="text-3xl mr-4">🚨</div>
              <h2 className="text-2xl font-bold text-gray-900">What If SuddyFlow Shuts Down or Gets Sold?</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <p className="text-gray-700 mb-4">
                Fair question. Here's our commitment:
              </p>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <p className="font-semibold text-blue-800 mb-2">If we ever shut down:</p>
                  <ul className="text-gray-700 space-y-2">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Minimum 90 days advance notice via email</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Full data export tools available throughout the notice period</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Prorated refunds for any unused subscription time</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <p className="font-semibold text-purple-800 mb-2">If we're acquired:</p>
                  <ul className="text-gray-700 space-y-2">
                    <li className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      <span>Your data privacy terms transfer to the new owner</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      <span>If privacy terms change, you get 60 days to export and leave</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      <span>We'll never do a silent handoff—you'll know what's happening</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Security - Actually Defined */}
          <div className="mb-12">
            <div className="flex items-center mb-4">
              <div className="text-3xl mr-4">🔐</div>
              <h2 className="text-2xl font-bold text-gray-900">Security—Actually Defined</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <p className="text-gray-700 mb-4">
                "Secure" is meaningless without specifics. Here's exactly how we protect your data:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="font-semibold text-gray-800 mb-2">🏗️ Infrastructure</p>
                  <ul className="text-gray-700 text-sm space-y-1">
                    <li>• Microsoft Azure cloud hosting</li>
                    <li>• Azure SQL with TDE (encryption at rest)</li>
                    <li>• HTTPS/TLS 1.3 (encryption in transit)</li>
                    <li>• US-based data centers</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="font-semibold text-gray-800 mb-2">🔑 Access Control</p>
                  <ul className="text-gray-700 text-sm space-y-1">
                    <li>• Multi-tenant isolation (your data is walled off)</li>
                    <li>• Role-based permissions (employees vs owners)</li>
                    <li>• Secure password hashing (bcrypt)</li>
                    <li>• Session management & timeout</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="font-semibold text-gray-800 mb-2">🚨 Breach Response</p>
                  <ul className="text-gray-700 text-sm space-y-1">
                    <li>• Affected users notified within 72 hours</li>
                    <li>• Clear explanation of what happened</li>
                    <li>• Steps we're taking to fix it</li>
                    <li>• What you should do (if anything)</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="font-semibold text-gray-800 mb-2">👤 Your Responsibility</p>
                  <ul className="text-gray-700 text-sm space-y-1">
                    <li>• Use a strong, unique password</li>
                    <li>• Don't share login credentials</li>
                    <li>• Log out on shared devices</li>
                    <li>• Report suspicious activity to us</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Integration Risks */}
          <div className="mb-12">
            <div className="flex items-center mb-4">
              <div className="text-3xl mr-4">🔌</div>
              <h2 className="text-2xl font-bold text-gray-900">Third-Party Integrations</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <p className="text-gray-700 mb-4">
                SuddyFlow connects with payment processors, email providers, and other services. 
                Here's how responsibility works:
              </p>
              <div className="bg-white rounded-lg p-4 border border-yellow-200">
                <p className="font-semibold text-yellow-800 mb-2">We're responsible for:</p>
                <ul className="text-gray-700 space-y-1">
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-2">✓</span>
                    <span>Building secure connections to third-party services</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-2">✓</span>
                    <span>Monitoring for failures and alerting you when something breaks</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-2">✓</span>
                    <span>Providing clear error messages so you know what's happening</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200 mt-4">
                <p className="font-semibold text-gray-800 mb-2">What's outside our control:</p>
                <ul className="text-gray-700 space-y-1">
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    <span>Third-party service outages (Stripe, email providers, etc.)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    <span>Changes to third-party pricing or terms</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">•</span>
                    <span>Issues with your third-party account credentials</span>
                  </li>
                </ul>
              </div>
              <p className="text-gray-600 text-sm mt-4 italic">
                We partner with established providers (Stripe, SendGrid, etc.) and avoid fly-by-night services. 
                If an integration isn't working, we'll help you troubleshoot.
              </p>
            </div>
          </div>

          {/* Who Built This */}
          <div className="mb-12">
            <div className="flex items-center mb-4">
              <div className="text-3xl mr-4">👷</div>
              <h2 className="text-2xl font-bold text-gray-900">Who's Behind SuddyFlow?</h2>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
              <p className="text-gray-700 mb-4">
                I'm Jon—a veteran and pressure washing business owner in the Lake Norman area. I got tired 
                of juggling spreadsheets, sticky notes, and five different apps just to keep track of 
                customers and jobs. So I built the tool I wished existed.
              </p>
              <p className="text-gray-700 mb-4">
                SuddyFlow isn't backed by investors or built by people who've never worked in the field. 
                It's built by someone who's been on the truck, quoted the jobs, and chased the payments—just like you.
              </p>
              <div className="flex items-center space-x-4">
                <span className="bg-red-600 text-white px-3 py-1 rounded text-sm font-semibold">
                  🇺🇸 Veteran-Owned
                </span>
                <span className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-semibold">
                  Built in NC
                </span>
              </div>
            </div>
          </div>

          {/* Still Have Questions */}
          <div className="bg-gray-900 rounded-lg p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-gray-300 mb-6">
              I'm happy to answer anything about how your data is handled. 
              No PR team, no runaround—just a straight answer.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a 
                href="mailto:support@suddyflow.com" 
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Email Me Directly
              </a>
              {!comingSoonMode && (
                <Link 
                  to="/contact" 
                  className="bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  Contact Page
                </Link>
              )}
            </div>
          </div>

        </div>
      </section>

      {comingSoonMode ? (
        <div className="bg-gray-900 py-8">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Link 
              to="/" 
              className="text-blue-400 hover:text-blue-300 font-semibold"
            >
              ← Back to SuddyFlow
            </Link>
            <p className="text-gray-500 text-sm mt-4">
              © {new Date().getFullYear()} Suddy Buckets LLC. All rights reserved.
            </p>
          </div>
        </div>
      ) : (
        <Footer />
      )}
    </div>
  );
};

export default TrustPage;